import React from "react";
import { partition } from "lodash";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";

import { formatConcealedModelYear } from "../../../../commons/libs/formatters";
import { getBrandLogoUrl } from "../../../../commons/libs/resource-paths";
import { ProductType } from "../../../../commons/specs/product";
import { Brand } from "../../../../commons/types/brand";
import { GlobalLocationState } from "../../../../commons/types/location";
import * as icons from "../../../../resources/icons";
import BrandTile from "../../../commons/components/BrandTile/BrandTile";
import CenteredContent from "../../../commons/components/CenteredContent/CenteredContent";
import FlexLayout from "../../../commons/components/FlexLayout/FlexLayout";
import FlippableTile from "../../../commons/components/FlippableTile/FlippableTile";
import Icon from "../../../commons/components/Icon/Icon";
import Image from "../../../commons/components/Image/Image";
import TileGrid from "../../../commons/components/TileGrid/TileGrid";
import {
  generateBrandsGridConfig,
  getActiveModelYears,
  getLatestActiveModelYear,
  getPreviousActiveModelYear,
  shouldBeFlippableTile
} from "../../../commons/libs/brand";
import { shouldShowAllModelYears } from "../../../commons/libs/filters";
import { generatePath, useAppHistory } from "../../../commons/libs/hooks/use-app-routing";
import { useBrands } from "../../../commons/libs/queries/use-brands";
import useProductFilter from "../../../commons/libs/queries/use-product-filter";
import { useSettings } from "../../../commons/libs/queries/use-settings";
import { ROUTES } from "../../../commons/routes";

interface Props extends RouteComponentProps<EmptyObject, EmptyObject, GlobalLocationState> {
  productFilter: ReturnType<typeof useProductFilter>;
}

const BrandsContent = ({ productFilter }: Props) => {
  const { t } = useTranslation(["mobile"]);
  const history = useAppHistory();

  const settings = useSettings();
  const brands = useBrands();

  const shouldModelYearsBeConcealed = settings.initialized.customization.modelYearDisplay.concealed;
  const modelYearDisplayTerm = settings.initialized.customization.modelYearDisplay.term;

  const formatModelYear = React.useCallback(
    (modelYear: number): string =>
      shouldModelYearsBeConcealed ? formatConcealedModelYear(modelYear, modelYearDisplayTerm) : modelYear.toString(),
    [shouldModelYearsBeConcealed, modelYearDisplayTerm]
  );

  const showAllModelYears = shouldShowAllModelYears(
    settings.assortmentFilterSettings.showAllModelYears,
    settings.assortmentFilterSettings.selectAssortmentAutomatically
  );

  const goToProductFinder = async (brand: Brand, hrefToProducts: string, modelYear: number) => {
    const activeModelYearsForBrand = getActiveModelYears(brand);

    if (!showAllModelYears) {
      const isModelYearCurrentActiveYear = modelYear === getLatestActiveModelYear(brand);
      productFilter.setDefaultActiveFiltersForBrand(brand.key, {
        overrides: {
          modelYear: isModelYearCurrentActiveYear ? [] : [modelYear]
        }
      });
    } else {
      productFilter.setDefaultActiveFiltersForBrand(brand.key, {
        overrides: {
          modelYear: activeModelYearsForBrand
        }
      });
    }

    history.push(hrefToProducts);
  };

  const handleClick = (brand: Brand, hrefToProducts: string) => {
    goToProductFinder(brand, hrefToProducts, getLatestActiveModelYear(brand));
  };

  const [highlightedBrands, nonHighlightedBrands] = partition(brands.active, "highlighted");

  const brandsGridConfig = generateBrandsGridConfig({
    brandCount: brands.activeCount,
    shouldReduceColumnCount: true
  });

  const highlightedBrandsGridConfig = generateBrandsGridConfig({
    brandCount: brands.highlightedCount,
    brandsKind: "highlighted",
    shouldReduceColumnCount: true
  });

  const nonHighlightedBrandsGridConfig = generateBrandsGridConfig({
    brandCount: nonHighlightedBrands.length,
    brandsKind: "nonHighlighted",
    shouldReduceColumnCount: true
  });

  const isLargeBrandTile: boolean = brands.activeCount <= 2;

  const handleClickWithCurrentModelYear = (brand: Brand, hrefToProducts: string) => handleClick(brand, hrefToProducts);
  const handleClickWithPreviousModelYear = (brand: Brand, hrefToProducts: string) =>
    goToProductFinder(brand, hrefToProducts, getPreviousActiveModelYear(brand));

  const getHighlightedBadge = (highlighted?: boolean) =>
    highlighted ? <Icon source={icons.IconMediumHighlight} size="l" /> : undefined;

  const renderBrands = (brands: Brand<ProductType>[], brandTileHeight: "default" | "l", highlighted?: boolean) =>
    brands.map(brand => {
      const latestActiveModelYear = getLatestActiveModelYear(brand);
      const previousActiveModelYear = getPreviousActiveModelYear(brand);

      return shouldBeFlippableTile(brand, showAllModelYears) ? (
        <FlippableTile
          key={brand.key}
          height={brandTileHeight}
          frontSide={flip => (
            <BrandTile
              height={brandTileHeight}
              badge={getHighlightedBadge(highlighted)}
              year={formatModelYear(latestActiveModelYear)}
              href={generatePath(ROUTES.PRODUCT_FINDER.BRAND, { brand: brand.key })}
              onClick={(_event: React.MouseEvent<HTMLElement>, hrefToProducts: string) =>
                handleClickWithCurrentModelYear(brand, hrefToProducts)
              }
              onDoubleClick={(_event: React.MouseEvent<HTMLElement>, hrefToProducts: string) =>
                handleClickWithCurrentModelYear(brand, hrefToProducts)
              }
              image={<Image multiplyBlendMode src={getBrandLogoUrl(brand.key)} />}
              isFlippable
              onFlip={flip}
            />
          )}
          backSide={flip => (
            <BrandTile
              height={brandTileHeight}
              badge={getHighlightedBadge(highlighted)}
              year={formatModelYear(previousActiveModelYear)}
              href={generatePath(ROUTES.PRODUCT_FINDER.BRAND, { brand: brand.key })}
              onClick={(_event: React.MouseEvent<HTMLElement>, hrefToProducts: string) => {
                handleClickWithPreviousModelYear(brand, hrefToProducts);
              }}
              onDoubleClick={(_event: React.MouseEvent<HTMLElement>, hrefToProducts: string) => {
                handleClickWithPreviousModelYear(brand, hrefToProducts);
              }}
              image={<Image multiplyBlendMode src={getBrandLogoUrl(brand.key)} />}
              isBackside
              isCloseable
              onClose={flip}
            />
          )}
        />
      ) : (
        <BrandTile
          height={brandTileHeight}
          badge={getHighlightedBadge(highlighted)}
          key={brand.key}
          year={!showAllModelYears ? formatModelYear(latestActiveModelYear) : undefined}
          href={generatePath(ROUTES.PRODUCT_FINDER.BRAND, { brand: brand.key })}
          onClick={(_event: React.MouseEvent<HTMLElement>, hrefToProducts: string) =>
            handleClickWithCurrentModelYear(brand, hrefToProducts)
          }
          onDoubleClick={(_event: React.MouseEvent<HTMLElement>, hrefToProducts: string) =>
            handleClickWithCurrentModelYear(brand, hrefToProducts)
          }
          image={<Image multiplyBlendMode src={getBrandLogoUrl(brand.key)} />}
        />
      );
    });

  const renderTileGrids = () => {
    if (highlightedBrands.length > 0) {
      return (
        <FlexLayout direction="column" alignItems="center" gap="xxxl">
          <TileGrid size="l" columns={highlightedBrandsGridConfig.columnCount}>
            {renderBrands(highlightedBrands, "l", true)}
          </TileGrid>
          <TileGrid size="s" columns={nonHighlightedBrandsGridConfig.columnCount}>
            {renderBrands(nonHighlightedBrands, "default")}
          </TileGrid>
        </FlexLayout>
      );
    } else {
      return (
        <TileGrid columns={brandsGridConfig.columnCount} shadow={brandsGridConfig.withShadow}>
          {renderBrands(brands.active, isLargeBrandTile ? "l" : "default")}
        </TileGrid>
      );
    }
  };

  return (
    <CenteredContent>
      {brands.activeCount > 0 ? (
        renderTileGrids()
      ) : (
        <>{t("mobile:brandsContentProductFinderOverview.noBrandsActivated")}</>
      )}
    </CenteredContent>
  );
};

export default BrandsContent;
